import { useState } from 'react';
import toast from 'react-hot-toast';
import { useClients } from '../../hooks/useClients';
import { useConfirm } from '../../hooks/useConfirm';
import { clientService } from '../../services/clientService';
import { parseAddress } from '../../utils/addressParser';
import { ClientList } from './ClientList';
import { ClientEditor } from './ClientEditor';
import { HarvestClientImportModal } from './HarvestClientImportModal';
import type { Client } from '@shared/types';

export function ClientManager() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  const { clients, loading, reload } = useClients({
    search,
    status: statusFilter as any,
  });

  const { confirm, ConfirmDialog } = useConfirm();

  const handleCreateNew = () => {
    setSelectedClient(null);
    setIsCreating(true);
  };

  const handleEdit = (client: Client) => {
    setSelectedClient(client);
    setIsCreating(false);
  };

  const handleSave = async (client: Partial<Client>) => {
    setSaving(true);
    const toastId = toast.loading(selectedClient ? 'Updating client...' : 'Creating client...');
    
    try {
      console.log('Saving client:', client); // Debug
      
      if (selectedClient) {
        const updated = await clientService.updateClient(selectedClient.id, client);
        console.log('Client updated:', updated); // Debug
        toast.success('Client updated successfully!', { id: toastId });
      } else {
        const created = await clientService.createClient(client);
        console.log('Client created:', created); // Debug
        toast.success('Client created successfully!', { id: toastId });
      }
      
      setSelectedClient(null);
      setIsCreating(false);
      
      // Reload the clients list
      await reload();
    } catch (error) {
      console.error('Failed to save client:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Failed to save client: ${errorMessage}`, { id: toastId });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    const confirmed = await confirm({
      title: 'Delete Client',
      message: 'Are you sure you want to delete this client? This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger',
    });

    if (!confirmed) return;
    
    const toastId = toast.loading('Deleting client...');
    
    try {
      await clientService.deleteClient(id);
      toast.success('Client deleted successfully!', { id: toastId });
      reload();
    } catch (error) {
      console.error('Failed to delete client:', error);
      toast.error('Failed to delete client.', { id: toastId });
    }
  };

  const handleCancel = () => {
    setSelectedClient(null);
    setIsCreating(false);
  };

  const handleImportFromHarvest = () => {
    setShowImportModal(true);
  };

  const handleImportClient = async (harvestClient: any) => {
    const toastId = toast.loading('Importing client from Harvest...');
    
    try {
      // Parse the Harvest address
      const parsedAddress = parseAddress(harvestClient.address || '');
      
      // Create client with Harvest data including parsed address
      const clientData = {
        name: harvestClient.name,
        addressLine1: parsedAddress.addressLine1,
        addressLine2: parsedAddress.addressLine2,
        city: parsedAddress.city,
        state: parsedAddress.state,
        zip: parsedAddress.zip,
        status: harvestClient.is_active ? 'active' : 'inactive',
        externalSource: 'harvest',
        externalId: String(harvestClient.id),
        externalData: {
          harvestId: harvestClient.id,
          currency: harvestClient.currency,
          importedAt: new Date().toISOString(),
          originalAddress: harvestClient.address,
        },
        notes: `Imported from Harvest on ${new Date().toLocaleDateString()}`,
      };

      const created = await clientService.createClient(clientData);
      toast.success(`Client "${harvestClient.name}" imported successfully!`, { id: toastId });
      
      // Import contacts for this client
      const contactToastId = toast.loading('Syncing client contacts from Harvest...');
      try {
        console.log('🔄 Starting contact import:', {
          createdClientId: created.id,
          harvestClientId: harvestClient.id,
          harvestClientIdType: typeof harvestClient.id,
        });
        
        const contactsResult = await clientService.importHarvestContacts(
          created.id, 
          String(harvestClient.id) // Ensure it's a string
        );
        
        console.log('📊 Contact import result:', contactsResult);
        
        if (contactsResult.imported > 0) {
          toast.success(
            `Synced ${contactsResult.imported} contact${contactsResult.imported !== 1 ? 's' : ''}`,
            { id: contactToastId }
          );
        } else if (contactsResult.skipped > 0) {
          toast.success(`All ${contactsResult.skipped} contacts already imported`, { id: contactToastId });
        } else {
          toast('No contacts found for this client', { id: contactToastId, icon: 'ℹ️' });
        }
      } catch (contactError) {
        console.error('❌ Failed to import contacts:', contactError);
        toast.error('Client imported but contacts sync failed', { id: contactToastId });
      }
      
      // Reload clients and select the newly imported one
      await reload();
      
      // Fetch the updated client to see if contacts were saved
      const updatedClient = await clientService.getClient(created.id);
      console.log('📄 Client after reload:', {
        id: updatedClient.id,
        name: updatedClient.name,
        hasExternalData: !!updatedClient.externalData,
        contactCount: updatedClient.externalData?.contacts?.length || 0,
        externalData: updatedClient.externalData,
      });
      
      setSelectedClient(updatedClient);
      setIsCreating(false);
    } catch (error) {
      console.error('Failed to import client:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Failed to import client: ${errorMessage}`, { id: toastId });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Client Management</h1>
          <p className="mt-2 text-gray-600">
            Manage your clients and customer relationships
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Client List */}
          <div>
            <ClientList
              clients={clients}
              loading={loading}
              search={search}
              onSearchChange={setSearch}
              statusFilter={statusFilter}
              onStatusFilterChange={setStatusFilter}
              onCreateNew={handleCreateNew}
              onImportFromHarvest={handleImportFromHarvest}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          </div>

          {/* Client Editor */}
          <div>
            {(selectedClient || isCreating) && (
              <ClientEditor
                client={selectedClient}
                onSave={handleSave}
                onCancel={handleCancel}
                saving={saving}
              />
            )}
            {!selectedClient && !isCreating && (
              <div className="bg-white shadow-sm rounded-lg p-8 text-center text-gray-500">
                Select a client to edit or create a new one
              </div>
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog />
      
      <HarvestClientImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImport={handleImportClient}
      />
    </div>
  );
}

